// src/components/contacts/ContactTimelineDrawer.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Timeline, Button, Space, Tag, Typography, message, Empty, Popconfirm } from 'antd';
import { PlusOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { listContactActivities, deleteContactActivity, type ContactActivityDTO, type ContactDTO } from '../../services/contacts';
import ContactActivityDrawer from './ContactActivityDrawer';
import { renderAntdIcon } from '../../utils/antdIcon';

type Props = {
  open: boolean;
  contact: ContactDTO | null;
  onClose: () => void;
  onRefresh?: () => void; // richiama la lista contatti esterna
};

const { Text } = Typography;

// piccola palette per dare un colore stabile per typeId
const colors = ['blue', 'green', 'gold', 'purple', 'magenta', 'volcano', 'geekblue', 'lime', 'cyan'];

const ContactTimelineDrawer: React.FC<Props> = ({ open, contact, onClose, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ContactActivityDTO[]>([]);
  const [activityOpen, setActivityOpen] = useState(false);

  const contactId = contact?.id ?? null;

  const load = async () => {
    if (!contactId) return;
    try {
      setLoading(true);
      const res = await listContactActivities(contactId);
      // ordina desc per occurredAt
      const sorted = (res || []).slice().sort(
        (a, b) => dayjs(b.occurredAt).valueOf() - dayjs(a.occurredAt).valueOf()
      );
      setItems(sorted);
    } catch (e: any) {
      message.error(e?.message || 'Errore nel caricamento delle attività');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && contactId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contactId]);

  // colore stabile per typeId
  const colorByTypeId = useMemo(() => {
    const map = new Map<number, string>();
    items.forEach((a) => {
      if (!map.has(a.typeId)) {
        map.set(a.typeId, colors[map.size % colors.length]);
      }
    });
    return map;
  }, [items]);

  const title = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div>
        <div style={{ fontWeight: 600 }}>
          {contact?.businessName || 'Contatto'}
        </div>
        {(contact?.email || contact?.phone) && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {contact?.email} {contact?.email && contact?.phone ? ' • ' : ''} {contact?.phone}
          </Text>
        )}
      </div>
    </div>
  );

  const handleDeleteActivity = async (activityId: number) => {
    if (!contactId) return;
    try {
      await deleteContactActivity(contactId, activityId);
      message.success('Attività eliminata');
      await load();
      onRefresh?.(); // aggiorna lista contatti per lastActivityAt
    } catch (e: any) {
      message.error(e?.message || 'Errore durante eliminazione attività');
    }
  };

  return (
    <>
      <Drawer
        title={title}
        open={open}
        onClose={onClose}
        width={700}
        destroyOnClose
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} disabled={!contactId} />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setActivityOpen(true)}
              disabled={!contactId}
            >
              Nuova attività
            </Button>
          </Space>
        }
      >
        {items.length === 0 && !loading ? (
          <Empty description="Nessuna attività registrata" />
        ) : (
          <Timeline
            mode="left"
            pending={loading ? 'Caricamento…' : undefined}
            items={items.map((a) => ({
              color: colorByTypeId.get(a.typeId) || 'gray',
              label: dayjs(a.occurredAt).format('DD/MM/YYYY HH:mm'),
              children: (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <Space size={8} wrap>
                      <Tag color={colorByTypeId.get(a.typeId) || 'default'} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 16, lineHeight: 0 }}>
                          {renderAntdIcon(a.typeIcon)}
                        </span>
                        <span>{a.typeLabel || 'Attività'}</span>
                      </Tag>
                      <span style={{ color: '#666' }}>
                        {a.notes || <span style={{ color: '#bbb' }}>—</span>}
                      </span>
                    </Space>
                  </div>
                  <div>
                    <Popconfirm
                      title="Eliminare questa attività?"
                      okText="Elimina"
                      cancelText="Annulla"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDeleteActivity(a.id)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                </div>
              ),
            }))}
          />
        )}
      </Drawer>

      {/* Shortcut "Nuova attività" dentro al drawer */}
      <ContactActivityDrawer
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        contactId={contactId}
        onCreated={async () => {
          await load();
          onRefresh?.();
        }}
      />
    </>
  );
};

export default ContactTimelineDrawer;
